import React, { useEffect, useState } from 'react';
import {
  FiPlus, FiSend, FiClock, FiCheck, FiX, FiTrash2, FiRefreshCw,
  FiLoader, FiEdit3, FiCheckCircle, FiAlertCircle,
} from 'react-icons/fi';
import { supabase } from '@/integrations/supabase/client';

/**
 * Thread Composer — admin-only UI that:
 *   1) Takes a topic + angle from the user
 *   2) Calls /api/thread-composer (Fable 5 splits into N tweets)
 *   3) Shows the drafted tweets for edit
 *   4) User picks a schedule time → status='approved'
 *   5) /api/thread-post cron picks it up and posts sequentially
 *
 * State lives in Supabase (twitter_thread_schedule + twitter_thread_tweets)
 * so the same thread survives page reload / device switch.
 */

interface ThreadRow {
  id: string;
  topic: string;
  angle: string | null;
  tweet_count: number;
  status: 'draft' | 'approved' | 'posting' | 'posted' | 'failed' | 'canceled';
  scheduled_at: string | null;
  first_tweet_id: string | null;
  posted_at: string | null;
  error_message: string | null;
  created_at: string;
}

interface TweetRow {
  id: string;
  thread_id: string;
  position: number;
  text: string;
  posted_id: string | null;
  posted_at: string | null;
}

const ThreadComposer: React.FC<{ onSaved?: () => void; threadPipeline?: any }> = ({ onSaved, threadPipeline }) => {
  const [topic, setTopic] = useState('');
  const [angle, setAngle] = useState('discipline');
  const [count, setCount] = useState(6);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [threads, setThreads] = useState<ThreadRow[]>([]);
  const [selectedThread, setSelectedThread] = useState<ThreadRow | null>(null);
  const [selectedTweets, setSelectedTweets] = useState<TweetRow[]>([]);
  const [loadingThreads, setLoadingThreads] = useState(false);
  const [editedText, setEditedText] = useState<Record<string, string>>({});
  const [scheduleInput, setScheduleInput] = useState('');
  const [savingSchedule, setSavingSchedule] = useState(false);

  const loadThreads = async () => {
    setLoadingThreads(true);
    const { data } = await supabase
      .from('twitter_thread_schedule')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(30);
    setThreads((data || []) as any);
    setLoadingThreads(false);
  };

  const loadThreadTweets = async (threadId: string) => {
    const { data } = await supabase
      .from('twitter_thread_tweets')
      .select('*')
      .eq('thread_id', threadId)
      .order('position');
    const rows = (data || []) as any;
    setSelectedTweets(rows);
    const edits: Record<string, string> = {};
    rows.forEach((t: TweetRow) => { edits[t.id] = t.text; });
    setEditedText(edits);
  };

  useEffect(() => { loadThreads(); }, []);

  const generateThread = async () => {
    if (!topic.trim()) return;
    setGenerating(true);
    setError(null);
    try {
      const { data: session } = await supabase.auth.getSession();
      const token = session?.session?.access_token;
      const res = await fetch('/api/growth-api?action=compose-thread', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ topic: topic.trim(), angle, count: Number(count) }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json?.error || `HTTP ${res.status}`);
      setTopic('');
      await loadThreads();
      if (json.thread_id) {
        const row = { ...json.thread } as ThreadRow;
        setSelectedThread(row);
        await loadThreadTweets(json.thread_id);
      }
      onSaved?.();
    } catch (e: any) {
      setError(e?.message || String(e));
    } finally {
      setGenerating(false);
    }
  };

  const saveEditedTweet = async (tweet: TweetRow) => {
    const newText = editedText[tweet.id] ?? '';
    if (newText === tweet.text) return;
    await supabase
      .from('twitter_thread_tweets')
      .update({ text: newText })
      .eq('id', tweet.id);
    if (selectedThread) await loadThreadTweets(selectedThread.id);
  };

  const approveAndSchedule = async () => {
    if (!selectedThread) return;
    setSavingSchedule(true);
    try {
      const scheduledIso = scheduleInput
        ? new Date(scheduleInput).toISOString()
        : new Date().toISOString();
      await supabase
        .from('twitter_thread_schedule')
        .update({ status: 'approved', scheduled_at: scheduledIso })
        .eq('id', selectedThread.id);
      await loadThreads();
      setSelectedThread({ ...selectedThread, status: 'approved', scheduled_at: scheduledIso });
      onSaved?.();
    } finally {
      setSavingSchedule(false);
    }
  };

  const cancelThread = async () => {
    if (!selectedThread) return;
    if (!confirm('Cancel this thread? It will not post.')) return;
    await supabase
      .from('twitter_thread_schedule')
      .update({ status: 'canceled' })
      .eq('id', selectedThread.id);
    setSelectedThread(null);
    setSelectedTweets([]);
    await loadThreads();
    onSaved?.();
  };

  const deleteDraft = async (id: string) => {
    if (!confirm('Delete this draft thread permanently?')) return;
    await supabase.from('twitter_thread_schedule').delete().eq('id', id);
    if (selectedThread?.id === id) {
      setSelectedThread(null);
      setSelectedTweets([]);
    }
    await loadThreads();
    onSaved?.();
  };

  return (
    <div className="space-y-6">
      {/* Composer form */}
      <div className="rounded-2xl border border-cyan-500/20 bg-cyan-500/[0.03] p-5">
        <div className="flex items-center gap-2 mb-4">
          <FiPlus className="w-4 h-4 text-cyan-400" />
          <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-cyan-400">
            New thread from topic
          </span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-3">
          <input
            type="text"
            value={topic}
            onChange={(e) => setTopic(e.target.value)}
            placeholder="Topic — e.g. Why 90% of prop traders blow up"
            className="md:col-span-2 px-3 py-2 rounded-md bg-black/40 border border-white/10 text-slate-200 text-sm focus:border-cyan-500/60 focus:outline-none"
          />
          <select
            value={angle}
            onChange={(e) => setAngle(e.target.value)}
            className="px-3 py-2 rounded-md bg-black/40 border border-white/10 text-slate-200 text-sm focus:border-cyan-500/60 focus:outline-none"
          >
            <option value="discipline">Discipline / risk</option>
            <option value="macro-recap">Macro recap</option>
            <option value="lessons">Lessons learned</option>
            <option value="myths">Myths debunked</option>
            <option value="strategy">Strategy walkthrough</option>
          </select>
          <select
            value={count}
            onChange={(e) => setCount(Number(e.target.value))}
            className="px-3 py-2 rounded-md bg-black/40 border border-white/10 text-slate-200 text-sm focus:border-cyan-500/60 focus:outline-none"
          >
            {[4, 5, 6, 7, 8, 9, 10].map(n => (
              <option key={n} value={n}>{n} tweets</option>
            ))}
          </select>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={generateThread}
            disabled={!topic.trim() || generating}
            className="px-4 py-2 rounded-md bg-cyan-500/20 border border-cyan-500/40 text-cyan-100 text-sm font-mono hover:bg-cyan-500/30 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {generating ? <FiLoader className="animate-spin" /> : <FiSend />}
            {generating ? 'Generating with Fable 5…' : 'Generate thread'}
          </button>
          {error && (
            <span className="text-xs font-mono text-rose-400 flex items-center gap-1">
              <FiAlertCircle /> {error}
            </span>
          )}
        </div>
      </div>

      {/* Threads list + detail */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* List */}
        <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-4 lg:col-span-1">
          <div className="flex items-center justify-between mb-3">
            <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-slate-400">
              Threads
            </span>
            <button
              onClick={loadThreads}
              className="p-1 text-slate-500 hover:text-white"
              title="Refresh"
            >
              <FiRefreshCw className={`w-3.5 h-3.5 ${loadingThreads ? 'animate-spin' : ''}`} />
            </button>
          </div>
          {threads.length === 0 ? (
            <div className="text-xs text-gray-500 font-mono py-6 text-center">
              No threads yet. Generate one above.
            </div>
          ) : (
            <div className="space-y-1.5">
              {threads.map(t => (
                <button
                  key={t.id}
                  onClick={() => { setSelectedThread(t); loadThreadTweets(t.id); }}
                  className={`w-full text-left rounded-lg border p-2.5 transition-colors ${
                    selectedThread?.id === t.id
                      ? 'border-cyan-500/60 bg-cyan-500/10'
                      : 'border-white/10 bg-white/[0.02] hover:border-cyan-500/30 hover:bg-white/[0.04]'
                  }`}
                >
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <StatusBadge status={t.status} />
                    <span className="text-[10px] font-mono text-gray-500">
                      {t.tweet_count} tw
                    </span>
                  </div>
                  <div className="text-xs text-slate-300 line-clamp-2">{t.topic}</div>
                  <div className="mt-1 text-[10px] font-mono text-gray-500">
                    {t.scheduled_at
                      ? `→ ${new Date(t.scheduled_at).toLocaleString()}`
                      : `Created ${new Date(t.created_at).toLocaleDateString()}`}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Detail */}
        <div className="lg:col-span-2">
          {selectedThread ? (
            <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5 space-y-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <StatusBadge status={selectedThread.status} />
                    {selectedThread.error_message && (
                      <span className="text-[10px] font-mono text-rose-400 truncate max-w-[240px]">
                        {selectedThread.error_message}
                      </span>
                    )}
                  </div>
                  <div className="text-lg text-white font-semibold">{selectedThread.topic}</div>
                  <div className="text-[11px] font-mono text-gray-500 mt-0.5">
                    Angle: {selectedThread.angle || '—'} · Created {new Date(selectedThread.created_at).toLocaleString()}
                  </div>
                </div>
                <div className="flex flex-col gap-2 text-right">
                  {selectedThread.status === 'draft' && (
                    <button
                      onClick={() => deleteDraft(selectedThread.id)}
                      className="text-[11px] font-mono text-rose-400 hover:text-rose-300 flex items-center gap-1 justify-end"
                    >
                      <FiTrash2 className="w-3 h-3" /> Delete
                    </button>
                  )}
                  {selectedThread.status === 'approved' && (
                    <button
                      onClick={cancelThread}
                      className="text-[11px] font-mono text-amber-400 hover:text-amber-300 flex items-center gap-1 justify-end"
                    >
                      <FiX className="w-3 h-3" /> Cancel
                    </button>
                  )}
                </div>
              </div>

              {/* Tweets */}
              <div className="space-y-3">
                {selectedTweets.map(t => {
                  const chars = (editedText[t.id] ?? '').length;
                  const over = chars > 280;
                  return (
                    <div key={t.id} className="rounded-lg border border-white/10 bg-black/30 p-3">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-[10px] font-mono uppercase tracking-widest text-cyan-400">
                          {t.position} / {selectedTweets.length}
                        </span>
                        <div className="flex items-center gap-2 text-[10px] font-mono">
                          <span className={over ? 'text-rose-400' : chars > 260 ? 'text-amber-400' : 'text-gray-500'}>
                            {chars}/280
                          </span>
                          {t.posted_id && (
                            <a
                              href={`https://x.com/i/status/${t.posted_id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-emerald-400 hover:text-emerald-300"
                            >
                              posted ↗
                            </a>
                          )}
                        </div>
                      </div>
                      <textarea
                        value={editedText[t.id] ?? ''}
                        onChange={(e) => setEditedText({ ...editedText, [t.id]: e.target.value })}
                        onBlur={() => saveEditedTweet(t)}
                        disabled={selectedThread.status !== 'draft'}
                        rows={3}
                        className="w-full bg-transparent text-sm text-slate-200 resize-none focus:outline-none disabled:text-slate-400"
                      />
                    </div>
                  );
                })}
              </div>

              {/* Schedule / approve */}
              {selectedThread.status === 'draft' && (
                <div className="pt-3 border-t border-white/10">
                  <div className="flex items-center gap-3 flex-wrap">
                    <input
                      type="datetime-local"
                      value={scheduleInput}
                      onChange={(e) => setScheduleInput(e.target.value)}
                      className="px-3 py-1.5 rounded-md bg-black/40 border border-white/10 text-slate-200 text-sm focus:border-cyan-500/60 focus:outline-none"
                    />
                    <button
                      onClick={approveAndSchedule}
                      disabled={savingSchedule}
                      className="px-4 py-1.5 rounded-md bg-emerald-500/20 border border-emerald-500/40 text-emerald-100 text-sm font-mono hover:bg-emerald-500/30 disabled:opacity-40 flex items-center gap-2"
                    >
                      {savingSchedule ? <FiLoader className="animate-spin w-3 h-3" /> : <FiCheck className="w-3 h-3" />}
                      {scheduleInput ? 'Approve & schedule' : 'Approve & post ASAP'}
                    </button>
                    <span className="text-[10px] font-mono text-gray-500">
                      Empty = ASAP (next /api/thread-post cron run).
                    </span>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="rounded-2xl border border-dashed border-white/10 bg-white/[0.01] p-8 flex flex-col items-center justify-center text-center min-h-[300px]">
              <FiEdit3 className="w-8 h-8 text-slate-600 mb-2" />
              <div className="text-sm text-slate-400 font-mono">Select a thread on the left</div>
              <div className="text-xs text-slate-500 mt-1">or generate a new one above.</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const styles: Record<string, string> = {
    draft:    'bg-white/10 text-gray-300 border-white/20',
    approved: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40',
    posting:  'bg-amber-500/20 text-amber-300 border-amber-500/40 animate-pulse',
    posted:   'bg-cyan-500/20 text-cyan-300 border-cyan-500/40',
    failed:   'bg-rose-500/20 text-rose-300 border-rose-500/40',
    canceled: 'bg-slate-700/40 text-slate-400 border-slate-600/40',
  };
  return (
    <span className={`text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded border ${styles[status] || styles.draft}`}>
      {status}
    </span>
  );
};

export default ThreadComposer;
